import {
  ActionIcon,
  Group,
  Indicator,
  SegmentedControl,
  Tooltip,
} from '@mantine/core'
import { useQueryClient } from '@tanstack/react-query'
import { observer } from 'mobx-react-lite'

import { FaIcon } from '#components/FaIcon'
import { useMst } from '#hooks'

import {
  scriptQueryKey,
  snapshotHistoryQueryKey,
  snapshotQueryKey,
  useCurrentScriptSnapshotHasNewer,
  useSnapshotHistoryHasNewer,
} from './useSnapshotLandData'

import styles from './SnapshotFilter.module.css'

export const SnapshotFilter = observer(function HistoryFilter({
  scriptId,
}: {
  scriptId: string
}) {
  const queryClient = useQueryClient()
  const { filter, setFilter } = useMst().view.snapshotLand

  const hasNewerSnapshots = useSnapshotHistoryHasNewer({
    scriptId,
    refetchInterval: 60 * 1000,
  })

  const hasNewerScript = useCurrentScriptSnapshotHasNewer({
    scriptId,
    refetchInterval: 60 * 1000,
  })

  const hasNewer = hasNewerSnapshots || hasNewerScript

  const refreshHistory = async () => {
    queryClient.invalidateQueries({
      queryKey: snapshotHistoryQueryKey(scriptId),
    })

    // Unless the heartbeat has detected a newer script already, we need to
    // reload the script for changes before making the new snapshot
    if (!hasNewerScript) {
      await queryClient.invalidateQueries({
        queryKey: scriptQueryKey(scriptId),
      })
    }

    queryClient.invalidateQueries({
      queryKey: snapshotQueryKey({ snapshotId: 'current', scriptId }),
    })
  }

  return (
    <Group gap={4} wrap="nowrap">
      <Tooltip label="Refresh snapshots" position="bottom">
        <ActionIcon
          className={styles.refresh}
          variant="subtle"
          onClick={refreshHistory}
        >
          <Indicator color="red" size={6} offset={-1} disabled={!hasNewer}>
            <FaIcon
              icon="fa-arrow-rotate-right"
              style={{ transform: 'rotate(30deg)' }}
            />
          </Indicator>
        </ActionIcon>
      </Tooltip>
      <SegmentedControl
        classNames={styles}
        style={{ flexShrink: 0 }}
        value={filter}
        onChange={(value) => {
          setFilter(value === 'manual' ? 'manual' : 'all')
        }}
        size="xs"
        data={[
          {
            value: 'all',
            label: 'All',
          },
          {
            value: 'manual',
            label: 'Manual',
          },
        ]}
      />
    </Group>
  )
})
